import { FC } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Autocomplete, TextField, TextFieldProps, Typography } from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { DebouncedState } from 'use-debounce';

type Props = {
  name: string;
  label: string;
  counties: string[];
  debouncedCountiesRefetch: DebouncedState<(county: string) => void>;
  validValue: (value: string) => boolean;
};

const RHFCountySelect: FC<Props & TextFieldProps> = ({
  name,
  label,
  counties,
  debouncedCountiesRefetch,
  validValue,
  ...other
}) => {
  const { control, setValue, getValues } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          autoHighlight
          selectOnFocus
          clearOnEscape
          clearOnBlur
          options={counties}
          disabled={other.disabled}
          value={getValues(field.name) || null}
          defaultValue={getValues(field.name)}
          onChange={(_event, newValue) => setValue(field.name, newValue || '')}
          onInputChange={(_event, data) => {
            debouncedCountiesRefetch(data);
          }}
          onBlur={(_event) => {
            if (!validValue(getValues(field.name))) {
              setValue(field.name, '');
            }
          }}
          popupIcon={null}
          renderOption={(props, option, { inputValue }) => {
            const matches = match(option, inputValue);
            const parts = parse(option, matches);

            return (
              <li {...props}>
                {parts.map((part, index) => (
                  <>
                    {index !== 0 && parts[index - 1].text.slice(-1) === ' ' && (
                      <Typography component="pre" variant="subtitle2" key={index}>
                        {' '}
                      </Typography>
                    )}
                    <Typography
                      component="pre"
                      variant="subtitle2"
                      key={part.text}
                      fontWeight={part.highlight ? 700 : 400}
                    >
                      {part.text}
                    </Typography>
                    {index < parts.length - 1 && parts[index + 1].text.slice(0, 1) === ' ' && (
                      <Typography component="pre" variant="subtitle2" key={index + part.text}>
                        {' '}
                      </Typography>
                    )}
                  </>
                ))}
              </li>
            );
          }}
          renderInput={(params) => <TextField label={label} {...params} {...other} {...field} />}
        />
      )}
    />
  );
};

export default RHFCountySelect;
